import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { logAction } from '../_actions';
import { crudService } from '../_services/';
import DataTable from 'react-data-table-component';
import {
    Container,
    Card,
    CardHeader,
    CardBody,
    Row,
    Col,
    Input,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button
} from 'reactstrap';
import NavBar from '../_components/navbar';
import Moment from 'react-moment';

const sanitizeText = (input) => {
    if (input === null || input === undefined) return '';
    let s = String(input);
    // decode HTML entities / remove tags
    try {
        const div = document.createElement('div');
        div.innerHTML = s;
        s = div.textContent || div.innerText || s;
    } catch (e) {
        // fallback: leave as is
    }
    // remove control characters and normalize spaces
    // remove control characters and normalize spaces
    s = s.replace(/[\x00-\x1F\x7F]/g, ' ');
    s = s.replace(/\s+/g, ' ').trim();
    return s;
};

const parseLogs = (input) => {
    if (!input) return [];
    let s = String(input);
    let lines = s.split(/<br\s*\/?>|\n/i);
    return lines.map(l => {
        let text = l;
        try {
            const div = document.createElement('div');
            div.innerHTML = text;
            text = div.textContent || div.innerText || text;
        } catch (e) {}
        text = text.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, ' ').trim();
        return text;
    }).filter(l => l !== '');
};

const ExecucoesComponent = (props) => {
    const dispatch = useDispatch();

    const execucoes = useSelector(state => (state.log && state.log.log) ? state.log.log : []);
    const execucoesAndamento = useSelector(state => (state.log && state.log.execucoesAndamento) ? state.log.execucoesAndamento : []);
    const reduxState = useSelector(state => state);

    const [term, setTerm] = useState('');
    const [modalLogs, setModalLogs] = useState({ isOpen: false, row: null, loading: false, data: [] });

    const toggleModal = () => setModalLogs({ ...modalLogs, isOpen: !modalLogs.isOpen });

    const handleRowClicked = (row) => {
        setModalLogs({ isOpen: true, row, loading: true, data: [] });
        crudService.get(`log/execucoes/job?jobNome=${encodeURIComponent(row.job_nome)}`)
            .then(res => {
                setModalLogs(prev => ({ ...prev, loading: false, data: res.data || [] }));
            })
            .catch(err => {
                console.error('Erro ao buscar histórico da rotina:', err);
                setModalLogs(prev => ({ ...prev, loading: false }));
            });
    };

    useEffect(() => {
        dispatch(logAction.getLogExecucoes());
        dispatch(logAction.getLogExecucoesAndamento());
    }, [dispatch]);

    const handleRefresh = () => {
        dispatch(logAction.getLogExecucoes());
        dispatch(logAction.getLogExecucoesAndamento());
    };

    const combinedExe = useMemo(() => {
        const listAndamento = (execucoesAndamento || []).map(item => ({ ...item, em_execucao: true }));
        const map = new Map();
        // adiciona andamento primeiro
        for (const it of listAndamento) {
            if (it && it.id !== undefined && it.id !== null) {
                map.set(it.id, it);
            } else {
                // fallback: usa chave por job_nome+data_inicio
                map.set(`${it.job_nome}_${it.data_inicio}`, it);
            }
        }

        for (const it of (execucoes || [])) {
            const key = (it && it.id !== undefined && it.id !== null) ? it.id : `${it.job_nome}_${it.data_inicio}`;
            if (!map.has(key)) {
                map.set(key, { ...it, em_execucao: false });
            }
        }
        return Array.from(map.values());
    }, [execucoes, execucoesAndamento]);

    // filtro de pesquisa sobre a lista combinada
    const filtered = useMemo(() => {
        if (!term) return combinedExe || [];
        const t = term.toLowerCase();
        return (combinedExe || []).filter(item =>
            sanitizeText(item.job_nome).toLowerCase().includes(t) ||
            sanitizeText(item.ultimo_status).toLowerCase().includes(t) ||
            sanitizeText(item.resultado).toLowerCase().includes(t)
        );
    }, [combinedExe, term]);

    const renderResultadoBadge = (row) => {
        // se está em execução, destaca primeiro
        if (row.em_execucao) {
            return <Badge color="warning">EM ANDAMENTO</Badge>;
        }
        const txt = (sanitizeText(row.resultado) || sanitizeText(row.ultimo_status) || '').toLowerCase();
        if (txt.includes('sucesso')) return <Badge color="success">SUCESSO</Badge>;
        if (txt.includes('erro')) return <Badge color="danger">ERRO</Badge>;
        const label = sanitizeText(row.resultado) || sanitizeText(row.ultimo_status) || '-';
        return <Badge color="secondary">{label}</Badge>;
    };

    const columns = [
        {
            name: 'Rotina',
            selector: row => sanitizeText(row.job_nome),
            sortable: true,
            wrap: true,
            grow: 2,
            cell: row => <div title={sanitizeText(row.job_nome)}>{sanitizeText(row.job_nome)}</div>
        },
        {
            name: 'Inicio',
            selector: row => row.data_inicio || '',
            sortable: true,
            cell: row => row.data_inicio ? <Moment format="DD/MM/YYYY HH:mm">{row.data_inicio}</Moment> : ''
        },
        {
            name: 'Fim',
            selector: row => row.data_fim || '',
            sortable: true,
            cell: row => row.data_fim ? <Moment format="DD/MM/YYYY HH:mm">{row.data_fim}</Moment> : ''
        },
        {
            name: 'Status',
            selector: row => sanitizeText(row.resultado) || sanitizeText(row.ultimo_status),
            sortable: true,
            cell: row => renderResultadoBadge(row),
            center: true,
            width: '160px'
        },
        {
            name: 'Ultimo status',
            selector: row => sanitizeText(row.ultimo_status),
            sortable: false,
            wrap: true,
            grow: 2,
            cell: row => <div style={{ whiteSpace: 'pre-wrap' }}>{sanitizeText(row.ultimo_status)}</div>
        }
    ];

    return (
        <div>
            {/* repassa tanto props do roteador quanto o estado Redux necessário para o NavBar */}
            <NavBar {...props} {...reduxState} />
            <Container className="mt-3">
                <Card>
                    <CardHeader>
                        <Row className="align-items-center">
                            <Col md={6}>
                                <h5 className="mb-0">Rotinas</h5>
                                <small className="text-muted">Ultimas rotinas (7 dias)</small>
                                {execucoesAndamento && execucoesAndamento.length > 0 ? (
                                    <div style={{ marginTop: 8 }}>
                                        <Badge color="warning">{execucoesAndamento.length} em andamento</Badge>
                                    </div>
                                ) : null}
                            </Col>
                            <Col md={6} className="d-flex justify-content-end">
                                <Input
                                    placeholder="Pesquisar por rotina ou status..."
                                    value={term}
                                    onChange={e => setTerm(e.target.value)}
                                    style={{ maxWidth: 320, marginRight: 8 }}
                                />
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <button className="btn btn-primary btn-sm" onClick={handleRefresh}>Atualizar</button>
                                </div>
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <DataTable
                            columns={columns}
                            data={filtered || []}
                            pagination
                            highlightOnHover
                            pointerOnHover
                            onRowClicked={handleRowClicked}
                            responsive
                            noHeader
                            striped
                            noDataComponent="Sem registros para mostrar"
                            defaultSortField="job_nome"
                        />
                    </CardBody>
                </Card>
            </Container>

            <Modal isOpen={modalLogs.isOpen} toggle={toggleModal} size="lg">
                <ModalHeader toggle={toggleModal}>
                    Logs da Rotina: {modalLogs.row ? sanitizeText(modalLogs.row.job_nome) : ''}
                </ModalHeader>
                <ModalBody style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    {modalLogs.loading ? (
                        <div className="text-center p-4">Carregando histórico...</div>
                    ) : modalLogs.data && modalLogs.data.length > 0 ? (
                        modalLogs.data.map((exec, i) => (
                            <div key={i} style={{ marginBottom: '20px', borderBottom: '1px solid #dee2e6', paddingBottom: '15px' }}>
                                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                                    <Badge color={exec.resultado === 'SUCESSO' ? 'success' : (exec.resultado === 'ERRO' ? 'danger' : (exec.em_execucao ? 'warning' : 'secondary'))}>
                                        {exec.resultado || (exec.em_execucao ? 'EM ANDAMENTO' : '-')}
                                    </Badge>
                                    <small className="text-muted">
                                        <strong>Início:</strong> {exec.data_inicio ? <Moment format="DD/MM/YYYY HH:mm:ss">{exec.data_inicio}</Moment> : '-'}
                                    </small>
                                    <small className="text-muted">
                                        <strong>Fim:</strong> {exec.data_fim ? <Moment format="DD/MM/YYYY HH:mm:ss">{exec.data_fim}</Moment> : '-'}
                                    </small>
                                </div>
                                
                                {parseLogs(exec.ultimo_status).length > 0 ? (
                                    <div style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#f4f4f4', padding: '10px', borderRadius: '4px' }}>
                                        <ul style={{ listStyleType: 'none', padding: 0, margin: 0, fontFamily: 'monospace', fontSize: '13px' }}>
                                            {parseLogs(exec.ultimo_status).map((log, idx) => (
                                                <li key={idx} style={{ padding: '4px 0', borderBottom: '1px solid #ddd', wordBreak: 'break-word' }}>{log}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : (
                                    <div style={{ fontStyle: 'italic', color: '#6c757d', fontSize: '13px' }}>Sem logs registrados nesta execução.</div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div style={{ fontStyle: 'italic', color: '#6c757d' }}>Nenhuma execução encontrada.</div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleModal}>Fechar</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default ExecucoesComponent;